import Vue from 'vue'
import {ActionTree, GetterTree, Module, MutationTree} from 'vuex';
import {RootState} from '../RootState';
import axios from "axios";
import {settings} from "@/plugins/settings";
import {IErrors} from "@/model/IErrors";
import responseHandler from "@/plugins/errorHandling";
import {IProduct, IProductForm} from "@/model/Product/productInterfaces";

interface ProductState {
  products: IProduct[];
  product: IProduct | null;
  errors: IErrors;
  total: number;
  currentPage: number;
  lastPage: number;
  perPage: number;
}

const state: ProductState = {
  products: [],
  product: null,
  errors: {},
  total: 0,
  currentPage: 1,
  lastPage: 1,
  perPage: 15,
};

const getters: GetterTree<ProductState, RootState> = {
  products: (state) => state.products,
  product: (state) => state.product,
  errors: (state) => state.errors,
  total: (state) => state.total,
  currentPage: (state) => state.currentPage,
  lastPage: (state) => state.lastPage,
  perPage: (state) => state.perPage,
};

const actions: ActionTree<ProductState, RootState> = {
  fetchProducts: ({commit}, spId: number): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'serviceProviders/' + spId + '/products',
      method: 'GET',
    }),
    commit,
    'productsLoaded'
  ),
  fetchAllProducts: ({commit}, params?: any): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'products',
      method: 'GET',
      params,
    }),
    commit,
    'productsLoaded'
  ),
  createProduct: ({commit}, data: { spId: number, product: IProductForm }): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'serviceProviders/' + data.spId + '/products',
      method: 'POST',
      data: data.product,
    }),
    commit,
    'productSaved'
  ),
  updateProduct: ({commit}, data: { spId: number, productId: number, product: IProductForm }): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'serviceProviders/' + data.spId + '/products/' + data.productId,
      method: 'PATCH',
      data: data.product,
    }),
    commit,
    'productSaved'
  ),
  deactivateProduct: ({commit}, data: { spId: number, productId: number }): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'serviceProviders/' + data.spId + '/products/' + data.productId,
      method: 'DELETE',
    }),
    commit,
    'productSaved'
  ),
  errors: ({commit}, errors: IErrors): any => {
    commit('errors', errors);
  },
  cleanError: ({commit}, index: any): any => {
    commit('cleanError', index);
  },
};

const mutations: MutationTree<ProductState> = {
  productsLoaded(state, payload) {
    if (Array.isArray(payload)) {
      state.products = payload;
      state.total = payload.length;
      state.currentPage = 1;
      state.lastPage = 1;
      state.perPage = payload.length;
    } else if (payload && typeof payload === 'object' && Array.isArray(payload.data)) {
      state.products = payload.data;
      state.total = payload.total || payload.data.length;
      state.currentPage = payload.current_page || 1;
      state.lastPage = payload.last_page || 1;
      state.perPage = payload.per_page || payload.data.length;
    } else {
      state.products = [];
      state.total = 0;
      state.currentPage = 1;
      state.lastPage = 1;
      state.perPage = 15;
    }
  },
  productSaved(state, payload) {
    state.product = payload;
    const index = state.products.findIndex((each) => each.id === payload.id);
    if (index >= 0) {
      Vue.set(state.products, index, payload);
    } else {
      state.products.unshift(payload);
    }
  },
  errors(state, payload: IErrors) {
    state.errors = payload;
  },
  cleanError(state, index: string) {
    Vue.delete(state.errors, index);
  },
};

const namespaced: boolean = true;

export const productModule: Module<ProductState, RootState> = {
  namespaced,
  state,
  getters,
  actions,
  mutations
};
